import { useEffect } from "react";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";
import { setFreelancerFormData, setRegisterPayment, doRegister, downloadAgreement, resetRegisterStateFreelancer, getPrices } from "../../../../store/register/actions";
import FreelancerUserData, { FreelancerUserFormData } from "./FreelancerUserData";
import Payment, { PaymentFormData } from "../Payment";
import RegisterComplete from "../RegisterComplete";
import { RegisterState, Registration, RegistrationPriceInfo } from "../../../../types/register";
import { RegisterCourseState } from "../../../../types/registerCourse";
import { getAddress, registerInformationKHN } from "../../../../store/register/actions";
import { setMailingLink } from "../../../../store/registerCourse/actions";
import { resetCourseRegister } from "../../../../store/registerCourse/actions";
import ConfirmationContainer from "../Confirmation/ConfirmationContainer";
import RegisterCourseContainer from "../Course/RegisterCourseContainer";
import { checkDiscountCode } from "../../../../store/discount/actions";
import { trimFields } from "../../../../helperfunctions/trimFields";
import { ApplicationState } from "../../../../store";
import { CheckoutPaymentFormData } from "../../../Checkout/CheckoutPayment";
import { getPaymentMethodsAllowed } from "../../../../helperfunctions/paymentMethod";
import { RegisterUserType } from "../../../../types/enum";

interface FreelancerStepperContainerState {
    registerCourse: RegisterCourseState;
    register: RegisterState;
    discountString?: string;

    getAddress: (houseNumber: string, zipCode: string, identifier: string, formValues: any) => void;

    registerInformationKHN: (khnId: number, zipCode: string, identifier: string, formValues: any) => void;
    setFreelancerFormData: (freelancerFormData: FreelancerUserFormData) => void;
    setRegisterPayment: (paymentData: PaymentFormData) => void;
    setMailingLink: (categoryId?: number, courseId?: string, location?: string, locationCode?: string) => void;
    checkDiscountCode: (discountCode: string) => void;

    doRegister: (registration: Registration[], paymentData: CheckoutPaymentFormData) => void;
    downloadAgreement: () => void;
    resetRegisterStateFreelancer: () => void;
    getPrices: (priceItems: RegistrationPriceInfo[]) => void;
    stateDiscountCode?: string;
}

interface FreelancerStepperContainerProps {
    categoryId?: number | undefined;
    courseId?: string | undefined;
    place?: string | undefined;
    locationCode?: string | undefined;
    discountCode?: string | undefined;

    currentStep: number;
    handleNext: () => void;
    handleBack: () => void;
}

const mapStateToProps = ({ registerCourse, register, discount: { discountCode, discountString } }: ApplicationState) => {
    return ({
        registerCourse,
        register,
        discountString,
        stateDiscountCode: discountCode,
    });
}

const mapDispatchToProps = (dispatch: Dispatch) => {
    return bindActionCreators({
        registerInformationKHN,
        setFreelancerFormData,
        setRegisterPayment,
        doRegister,
        getAddress,
        setMailingLink,
        resetCourseRegister,
        checkDiscountCode,
        downloadAgreement,
        resetRegisterStateFreelancer,
        getPrices,
    }, dispatch);
}

let FreelancerStepperContainer = ({
    resetRegisterStateFreelancer,
    downloadAgreement,
    registerInformationKHN,
    categoryId,
    courseId,
    place,
    locationCode,
    setMailingLink,
    handleNext,
    handleBack,
    currentStep,
    registerCourse,
    setFreelancerFormData,
    setRegisterPayment,
    doRegister,
    register,
    getAddress,
    checkDiscountCode,
    discountCode,
    discountString,
    getPrices,
    stateDiscountCode,
}: FreelancerStepperContainerProps & FreelancerStepperContainerState) => {
    useEffect(() => {
        categoryId && setMailingLink(categoryId, courseId, place, locationCode);
    }, [setMailingLink, categoryId, courseId, place, locationCode]);

    useEffect(() => {
        discountCode && checkDiscountCode(discountCode);
    }, [discountCode, checkDiscountCode])

    useEffect(() => {
        let priceItems: RegistrationPriceInfo[] = []
        if (registerCourse.course.type?.courseId) {
            const hasBooks = register.registerFreelancerUserFormData?.freelancerBooks || false
            const registrationPriceInfo: RegistrationPriceInfo = {
                courseId: registerCourse.course.type?.courseId,
                studentBooks: [hasBooks],
                discountCode: stateDiscountCode,
                khnId: register.registerFreelancerUserFormData?.khnId
            }
            priceItems.push(registrationPriceInfo)            
        }
        if (priceItems.length > 0) {
            getPrices(priceItems);
        }
    }, [register.registerFreelancerUserFormData, getPrices, registerCourse.course.type, stateDiscountCode]);

    useEffect(() => {
        register.registerPayment?.discountCode && checkDiscountCode(register.registerPayment?.discountCode);
    }, [register.registerPayment, checkDiscountCode])

    switch (currentStep) {
        case 0:
            return <RegisterCourseContainer
                handleNext={handleNext}
                userType={RegisterUserType.Freelancer}
            />;
        case 1:
            return <FreelancerUserData
                hasBookDiscount={registerCourse.course.type?.hasBookDiscount || false}
                priceIncludesBook={registerCourse.course.type?.priceIncludesBook ?? true}
                registerInformationKHN={registerInformationKHN}
                getAddress={getAddress}
                setRegisterFreelancerUserFormData={setFreelancerFormData}
                handleNext={handleNext}
                handleBack={handleBack}
            />;
        case 2:
            return <Payment
                prices={register.prices}
                downloadAgreement={downloadAgreement}
                onSubmitFunction={setRegisterPayment}
                handleNext={handleNext}
                handleBack={handleBack}
                checkDiscountCode={checkDiscountCode}
                discountString={discountString}
                paymentMethodsAllowed={getPaymentMethodsAllowed(register.prices)}
            />;
        case 3:
            return <ConfirmationContainer
                handleConfirm={() => {
                    resetRegisterStateFreelancer();
                    const { registerFreelancerUserFormData, registerPayment } = register;

                    if (registerCourse && registerFreelancerUserFormData && registerPayment) {
                        let registration: Registration = {
                            course: registerCourse.course,
                            relation: {
                                contact: {
                                    sex: registerFreelancerUserFormData.sex,
                                    initials: registerFreelancerUserFormData.initials,
                                    prefix: registerFreelancerUserFormData.prefix,
                                    firstName: registerFreelancerUserFormData.firstName,
                                    lastName: registerFreelancerUserFormData.lastName,
                                    birthDate: registerFreelancerUserFormData.birthDate.toString(),
                                    birthPlace: registerFreelancerUserFormData.birthPlace,
                                },
                                invoice: {
                                    byMail: !!registerFreelancerUserFormData.byMail,
                                    address: registerFreelancerUserFormData.adresInvoice,
                                    houseNumber: registerFreelancerUserFormData.numberInvoice,
                                    zipCode: registerFreelancerUserFormData.zipCodeInvoice,
                                    place: registerFreelancerUserFormData.placeInvoice,
                                    email: registerFreelancerUserFormData.emailInvoice,
                                },
                                companyName: registerFreelancerUserFormData.nameCompany,
                                zipCode: registerFreelancerUserFormData.zipCode,
                                houseNumber: registerFreelancerUserFormData.houseNumber,
                                address: registerFreelancerUserFormData.address,
                                place: registerFreelancerUserFormData.place,
                                workNumber: registerFreelancerUserFormData.workNumber,
                                mobileNumber: registerFreelancerUserFormData.mobileNumber,
                                email: registerFreelancerUserFormData.email,
                                password: registerFreelancerUserFormData.password,
                                studentId: registerFreelancerUserFormData.studentId,
                                autoFilled: registerFreelancerUserFormData.autoFilled,
                            },
                            students: [],
                            freelancerBooks: !!registerFreelancerUserFormData.freelancerBooks,
                        }

                        let totalPrice = (register.prices && register.prices.length > 0 && register.prices.reduce((a, b) => ({ price: (a.price || 0) + (b.price || 0) })).price) || 0;
                        (window as any).dataLayer.push({ 'event': 'register-freelancer', 'price': totalPrice, 'discountcode': stateDiscountCode, 'students': 1, 'course': registerCourse.course.type?.courseId });

                        doRegister([trimFields(registration)], { ...registerPayment, khnId: registerFreelancerUserFormData.khnId, discountCode: stateDiscountCode });
                        handleNext();
                    }
                }}
                handleBack={handleBack}
            />;
        default:
            return <RegisterComplete error={register.registerError} loading={register.registerLoading} />;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(FreelancerStepperContainer);